import React from 'react'
import { Link } from 'react-router-dom'
import { BookOpenIcon, UserGroupIcon, CurrencyDollarIcon, CodeBracketIcon, SparklesIcon, PhotoIcon } from '@heroicons/react/24/outline'

const HomePage: React.FC = () => {
  const features = [
    {
      icon: CodeBracketIcon,
      title: 'Story Branching',
      description: 'Create alternative storylines and explore different narrative paths with Git-inspired mechanics.',
      category: 'Core Feature'
    },
    {
      icon: UserGroupIcon,
      title: 'Collaborative Writing',
      description: 'Branch off other writers\' stories to explore new directions, build on each other\'s ideas, and grow narratives together.',
      category: 'Community'
    },
    {
      icon: CurrencyDollarIcon,
      title: 'Monetization',
      description: 'Earn StoryCoins and real revenue from your creative contributions and premium content.',
      category: 'Economics'
    },
    {
      icon: SparklesIcon,
      title: 'AI Story Writing',
      description: 'Don\'t want to write it yourself? Pick a point in a story, describe what should happen, and let an AI model write the chapter for you — paid with StoryCoins. Every AI-written story is clearly labelled.',
      category: 'AI'
    },
    {
      icon: PhotoIcon,
      title: 'AI Illustrations',
      description: 'Bring any moment to life with an illustration. The AI reads the surrounding text and suggests an image prompt — accept it, tweak it, or write your own, and the picture is generated on the spot. Always labelled as AI-generated.',
      category: 'AI'
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Hero Section */}
      <section className="relative min-h-hero bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative container-max section-padding py-24 lg:py-32">
          <div className="text-center max-w-4xl mx-auto">
            <div className="animate-fade-in-up">
              <h1 className="text-5xl lg:text-7xl font-bold tracking-tight text-white mb-6">
                Collaborative Storytelling
                <br />
                <span className="text-gradient bg-gradient-to-r from-white to-primary-200 bg-clip-text text-transparent">
                  Reimagined
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl leading-relaxed text-primary-100 max-w-3xl mx-auto mb-8">
                Create, branch, and merge stories with Git-inspired branching mechanics. 
                Collaborate with writers and artists in a dynamic storytelling ecosystem.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12">
                <Link
                  to="/storefront"
                  className="btn btn-lg bg-white text-primary-600 hover:bg-primary-50 shadow-strong hover:shadow-glow-lg transform hover:scale-105 transition-all duration-300"
                >
                  <BookOpenIcon className="w-5 h-5 mr-2" />
                  Explore Stories
                </Link>
                <Link
                  to="/editor"
                  className="px-8 py-4 text-lg font-semibold border-2 border-white text-white hover:bg-white hover:text-primary-600 rounded-lg transition-all duration-300 flex items-center justify-center"
                >
                  Start Writing <span aria-hidden="true" className="ml-2">→</span>
                </Link>
              </div>


            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              Platform Features
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Revolutionary storytelling tools that combine the best of collaborative development 
              with creative writing and artistic expression.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mr-3">
                        {feature.title}
                      </h3>
                      <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                        feature.category === 'AI'
                          ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                      }`}>
                        {feature.category}
                      </span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-800 dark:bg-neutral-900 text-neutral-300">
        <div className="container-max section-padding py-12">
          <div className="text-center">
            <p className="text-lg mb-4">Built with ❤️ for storytellers everywhere</p>
            <p className="text-sm text-neutral-500 mb-3">StoryBranch - Where stories branch and grow</p>
            <div className="flex justify-center gap-4 text-sm text-neutral-400">
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Use</Link>
              <span>·</span>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default HomePage
